import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'
import { AuthLogo } from '../AuthLogo/AuthLogo'
import { LoadingButton } from '@mui/lab'
import FormInput from 'components/form-elements/FormInput'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { object, string, TypeOf } from 'zod'
import { EMAIL_IS_INVALID } from 'const'
import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { changeAccessCode } from 'api/auth'
import NotificationSys from 'components/NotificationSystem'

const formSchema = object({
  email: string().min(1, 'Email is required').email(EMAIL_IS_INVALID),
  password: string().min(1, 'Password is required'),
  accessCode: string().min(1, 'Code is required'),
})

type FormType = TypeOf<typeof formSchema>

export const AuthPageChangeOrganization = () => {
  const navigate = useNavigate()

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      accessCode: '',
    },
  })

  const {
    formState: { isDirty, isValid },
    handleSubmit,
  } = methods

  const onClickForgot = useCallback(() => {
    navigate(routes.forgotPassword)
  }, [navigate])

  const onCancel = () => navigate(routes.signIn)

  const submitMutation = useMutation(changeAccessCode, {
    onSuccess: () => {
      NotificationSys.showSuccess(`Organization was change successfully.`)
      onCancel()
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = useCallback(
    (values: FormType) => {
      const { email, password, accessCode } = values
      submitMutation.mutate({ email, password, accessCode })
    },
    [submitMutation],
  )

  return (
    <FormProvider {...methods}>
      <AuthLogo mb={2} />
      <AuthFormTitle mb={2}>Change Organization.</AuthFormTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <Stack spacing={1.5}>
          <FormInput label="Email Address" autoComplete="email" name="email" required fullWidth />

          <FormInput
            label="Password"
            required
            fullWidth
            name="password"
            type="password"
            autoComplete="current-password"
          />

          <FormInput
            label="Institutional Access code"
            autoComplete="accessCode"
            name="accessCode"
            required
            fullWidth
          />
        </Stack>

        <Grid container alignItems="center" justifyContent="flex-end" mt={1}>
          <Grid item>
            <Typography
              variant="body1"
              color="grey.600"
              onClick={onClickForgot}
              sx={{ cursor: 'pointer' }}
            >
              Forgot password?
            </Typography>
          </Grid>
        </Grid>

        <Grid container justifyContent="center" mt={2}>
          <Grid item xs={12} sm={6}>
            <LoadingButton
              loading={submitMutation.isLoading}
              disabled={!isDirty || !isValid}
              fullWidth
              variant="contained"
              type="submit"
            >
              Use institutional access code
            </LoadingButton>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" mt={2}>
          <Grid item xs={12} sm={4}>
            <Button onClick={onCancel} variant="outlined" color="info" fullWidth>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}
